import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, UserPlus, UserMinus, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const ymOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const shift = (ym, n) => { const [y, m] = ym.split('-').map(Number); return ymOf(new Date(y, m - 1 + n, 1)); };
const fmt = (iso) => new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });

function PersonList({ items, tone, empty, kind, onOpen }) {
  if (!items.length) return <p className="text-xs text-slate-400 py-2">{empty}</p>;
  return (
    <ul className="space-y-1 max-h-48 overflow-y-auto sidebar-nav">
      {items.map((p) => (
        <li key={`${p.user_id}-${p.date}`}>
          <button type="button" onClick={() => onOpen(p.user_id)} className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors" data-testid={`employment-${kind}-${p.user_id}`}>
            <span className={`text-[11px] font-mono font-semibold w-12 shrink-0 ${tone}`}>{fmt(p.date)}</span>
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{p.full_name}</span>
            {p.rehire && <span className="text-[9px] font-bold uppercase rounded bg-emerald-100 text-emerald-700 px-1">yeniden</span>}
            <span className="ml-auto text-[10px] text-slate-400 truncate">{p.firm_name}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

// Seçili ayda işe başlayan ve işten ayrılan çalışanlar.
export default function EmploymentChangesWidget() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => ymOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('/employee-profile/employment-changes', { params: { month } })
      .then((r) => setData(r.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [month]);

  const [y, m] = month.split('-').map(Number);
  const open = (id) => navigate(`/dashboard/employees/${id}`);

  return (
    <div className="bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-5 h-full" data-testid="employment-changes-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">İşe Alınanlar / Ayrılanlar</h3>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setMonth((v) => shift(v, -1))} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="employment-prev-month"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-xs font-semibold w-24 text-center" data-testid="employment-month-label">{MONTHS[m - 1]} {y}</span>
          <button type="button" onClick={() => setMonth((v) => shift(v, 1))} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="employment-next-month"><ChevronRight className="h-4 w-4" /></button>
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-1.5 mb-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
            <UserPlus className="h-3.5 w-3.5" />İşe alınanlar <span className="rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-1.5" data-testid="employment-joined-count">{data?.joined?.length || 0}</span>
          </div>
          <PersonList items={data?.joined || []} tone="text-emerald-600" empty="Bu ay işe alınan yok" kind="joined" onOpen={open} />
        </div>
        <div>
          <div className="flex items-center gap-1.5 mb-1 text-xs font-semibold text-red-600 dark:text-red-400">
            <UserMinus className="h-3.5 w-3.5" />Ayrılanlar <span className="rounded-full bg-red-100 dark:bg-red-900/40 px-1.5" data-testid="employment-left-count">{data?.left?.length || 0}</span>
          </div>
          <PersonList items={data?.left || []} tone="text-red-600" empty="Bu ay ayrılan yok" kind="left" onOpen={open} />
        </div>
      </div>
    </div>
  );
}
